import type { PluginDefinition, UseBinding } from "@magic-md/core";
import {
    applyTemplateInputRefsToValue,
    buildDefaultedTemplateInputs,
    extractTemplateParams,
    getBlockingTemplateParams,
} from "@magic-md/core";
import { deepMergeConfigs } from "@magic-md/engine-client/config";

export function materializePluginUseDefinition(
    use: UseBinding,
    definitions: Map<string, PluginDefinition>,
    uses: readonly UseBinding[] = [],
): PluginDefinition | undefined {
    return materializePluginUse(use, definitions, new Map(uses.map((entry) => [entry.id, entry])), new Set());
}

function materializePluginUse(
    use: UseBinding,
    definitions: Map<string, PluginDefinition>,
    usesById: Map<string, UseBinding>,
    visitedUses: Set<string>,
): PluginDefinition | undefined {
    const sourceUse = usesById.get(use.source);
    if (sourceUse) {
        return materializeUseAlias(use, sourceUse, definitions, usesById, visitedUses);
    }

    const sourceDefinition = definitions.get(use.source);
    if (!sourceDefinition) return undefined;
    return materializeNodeUse(use, sourceDefinition);
}

function materializeUseAlias(
    use: UseBinding,
    sourceUse: UseBinding,
    definitions: Map<string, PluginDefinition>,
    usesById: Map<string, UseBinding>,
    visitedUses: Set<string>,
): PluginDefinition | undefined {
    if (visitedUses.has(sourceUse.id)) return undefined;
    visitedUses.add(sourceUse.id);
    try {
        const outerWith = buildDefaultedTemplateInputs(use.with, sourceUse.inputSchema);
        const sourceWith = sourceUse.with
            ? applyTemplateInputRefsToValue(sourceUse.with, outerWith) as Record<string, unknown>
            : undefined;
        const composedWith = mergeTemplateWith(use.with, sourceWith);
        const sourcePatch = sourceUse.patch
            ? applyTemplateInputRefsToValue(sourceUse.patch, outerWith) as Record<string, unknown>
            : undefined;
        const composedPatch = sourcePatch || use.patch
            ? deepMergeConfigs(
                cloneRecord(sourcePatch ?? {}),
                cloneRecord(use.patch ?? {}),
            )
            : undefined;
        const sourceBind = sourceUse.bind
            ? applyTemplateInputRefsToValue(sourceUse.bind, outerWith) as Record<string, unknown>
            : undefined;
        const composedBind = mergeStringRecord(sourceBind, use.bind);
        const propagatedWith = sourceAcceptsWith(sourceUse.source, definitions, usesById)
            ? composedWith
            : undefined;
        const params = use.template
            ? extractTemplateParams({
                with: propagatedWith,
                patch: composedPatch,
                bind: composedBind,
            })
            : [];

        const composedUse: UseBinding = {
            ...sourceUse,
            id: use.id,
            source: sourceUse.source,
            with: propagatedWith && Object.keys(propagatedWith).length > 0
                ? propagatedWith
                : undefined,
            patch: composedPatch && Object.keys(composedPatch).length > 0
                ? composedPatch
                : undefined,
            bind: composedBind,
            template: use.template === true ? true : undefined,
            params: params.length > 0 ? params : undefined,
            inputSchema: use.inputSchema,
            range: use.range,
            output: use.output,
            outputMode: use.outputMode,
            externalRef: use.externalRef,
        };
        return materializePluginUse(composedUse, definitions, usesById, visitedUses);
    } finally {
        visitedUses.delete(sourceUse.id);
    }
}

function materializeNodeUse(
    use: UseBinding,
    sourceDefinition: PluginDefinition,
): PluginDefinition {
    const templateValues = buildDefaultedTemplateInputs(use.with, sourceDefinition.inputSchema);
    const templateConfig = sourceDefinition.template
        ? applyTemplateInputRefsToValue(
            cloneRecord(sourceDefinition.config),
            templateValues,
        ) as Record<string, unknown>
        : cloneRecord(sourceDefinition.config);
    const mergedConfig = use.patch
        ? deepMergeConfigs(templateConfig, use.patch)
        : templateConfig;
    const params = extractTemplateParams(mergedConfig);
    const blockingParams = getBlockingTemplateParams(
        sourceDefinition.inputSchema,
        sourceDefinition.params,
        params,
    );
    const remainsTemplate = use.template === true || blockingParams.length > 0;
    return {
        ...sourceDefinition,
        id: use.id,
        config: mergedConfig,
        template: remainsTemplate,
        params: params.length > 0 ? params : undefined,
        ...(remainsTemplate && use.inputSchema ? { inputSchema: use.inputSchema } : {}),
        output: undefined,
        externalRef: use.externalRef,
        outputMode: use.outputMode,
    };
}

function sourceAcceptsWith(
    sourceId: string,
    definitions: Map<string, PluginDefinition>,
    usesById: Map<string, UseBinding>,
): boolean {
    if (usesById.has(sourceId)) return true;
    return definitions.get(sourceId)?.template === true;
}

function mergeTemplateWith(
    outer: Record<string, unknown> | undefined,
    template: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
    const result = {
        ...(outer ? cloneRecord(outer) : {}),
        ...(template ? cloneRecord(template) : {}),
    };
    return Object.keys(result).length > 0 ? result : undefined;
}

function mergeStringRecord(
    source: Record<string, unknown> | undefined,
    outer: Record<string, string> | undefined,
): Record<string, string> | undefined {
    const result: Record<string, string> = {};
    if (source) {
        for (const [key, value] of Object.entries(source)) {
            result[key] = typeof value === "string" ? value : JSON.stringify(value);
        }
    }
    if (outer) {
        for (const [key, value] of Object.entries(outer)) {
            result[key] = value;
        }
    }
    return Object.keys(result).length > 0 ? result : undefined;
}

function cloneRecord<T extends Record<string, unknown>>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}
